import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container, Typography, Card, CardContent, Grid, Box, Button,
  Chip, LinearProgress, CircularProgress, Alert, IconButton,
  Avatar, Divider, Paper, Tooltip, Stack, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputAdornment, Checkbox, FormControlLabel, MenuItem, Select
} from '@mui/material';
import {
  ArrowBack, LocationOn, Person, CheckCircle,
  Message, Description, AttachMoney, CalendarToday,
  TrendingUp, Add, Schedule, Photo, CloudUpload,
  Videocam, Download, Visibility, InsertDriveFile, Delete as DeleteIcon,
  Store, History, PersonAdd, AccountBalanceWallet, ExpandMore, ExpandLess,
  Gavel, Star, AccessTime, FactCheck
} from '@mui/icons-material';
import projectService from '../services/projectService';
import messagingService from '../services/messagingService';
import aiService from '../services/aiService';
import milestoneService from '../services/milestoneService';
import proposalService from '../services/proposalService';
import ProposalReviewDialog from '../components/proposals/ProposalReviewDialog';
import DialogCloseButton from '../components/common/DialogCloseButton';
import CallOutFeePanel from '../components/visits/CallOutFeePanel';
import MilestoneCreationDialog from '../components/milestones/MilestoneCreationDialog';
import ProjectAnalyticsPanel from '../components/projects/ProjectAnalyticsPanel';
import ChangeOrdersPanel from '../components/projects/ChangeOrdersPanel';
import MilestoneApproval from '../components/milestones/MilestoneApproval';
import AISimpleVerificationRequestDialog from '../components/verifiers/AISimpleVerificationRequestDialog';
import FileUploadDialog from '../components/projects/FileUploadDialog';
import MediaViewer from '../components/projects/MediaViewer';
import DailyDiarySection from '../components/projects/DailyDiarySection';
import FindContractorsDialog from '../components/contractors/FindContractorsDialog';
import ListingOutcomeDialog from '../components/projects/ListingOutcomeDialog';
import { FindVerifierDialog } from '../components/verifiers';
import EscrowSetupDialog from '../components/escrow/EscrowSetupDialog';
import ProjectHubCard from '../components/projects/ProjectHubCard';
import api from '../services/api';
import { formatHarare, formatHarareWindow, harareInputToIso, isoToHarareInput, windowStart } from '../utils/time';
import escrowService from '../services/escrowService';
import notificationService from '../services/notificationService';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { brand, status, border } from '../theme/colors';

const ASSESSMENT_LABELS = {
  site_visit: 'Site visit',
  building_plans: 'Building plans / drawings',
  photos_videos: 'Photos or videos',
  measurements: 'Measurements',
  material_specs: 'Material / finish specifications',
  boq_schedule: 'BOQ or material schedule',
  client_call: 'Client call',
  verifier_report: 'Verifier or PM report'
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // State management
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatingMilestones, setGeneratingMilestones] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestoneTimers, setMilestoneTimers] = useState({});
  const [proposals, setProposals] = useState([]);
  const [assessmentRequests, setAssessmentRequests] = useState([]);

  // Reloaded on its own after a call-out fee moves (paid, cancelled, released),
  // so the panel reflects the new state without a full page refresh.
  const loadAssessmentRequests = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}/assessment-requests`);
      setAssessmentRequests(res.data?.data?.assessmentRequests || []);
    } catch (err) {
      console.error('Error loading assessment requests:', err);
      setAssessmentRequests([]);
    }
  }, [id]);

  // Coming back from Stripe: flip the fee to held right away rather than
  // waiting on the webhook, then clean the query string.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('fee_session');
    const feeId = params.get('fee_id');
    const feeSource = params.get('fee_source') || 'assessment_request';
    if (!sessionId || !feeId) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/visit-fees/${feeSource}/${feeId}/confirm`, { sessionId });
      } catch (err) {
        console.error('Could not confirm the call-out fee payment:', err);
      }
      if (cancelled) return;
      await loadAssessmentRequests();
      ['fee_session', 'fee_id', 'fee_source'].forEach((k) => params.delete(k));
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showProposalReview, setShowProposalReview] = useState(false);
  const [assessmentResponseDialogOpen, setAssessmentResponseDialogOpen] = useState(false);
  const [selectedAssessmentRequest, setSelectedAssessmentRequest] = useState(null);
  const [assessmentResponse, setAssessmentResponse] = useState({
    responseMessage: '',
    providedItems: [],
    acceptedWindow: '',
    proposedWindows: ['', '', ''],
    decline: false,
    declineReason: ''
  });
  // Post-visit check-in ("did it happen, do you want their quotation?") —
  // opened from the email/WhatsApp link ?visitFeedback=<requestId>.
  const [visitFeedbackRequestId, setVisitFeedbackRequestId] = useState(null);
  const [visitFeedbackSaving, setVisitFeedbackSaving] = useState(false);
  const [rescheduleRequest, setRescheduleRequest] = useState(null);
  const [rescheduleQuoteId, setRescheduleQuoteId] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleError, setRescheduleError] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [quoteVisits, setQuoteVisits] = useState([]);
  const [markingQuoteVisitId, setMarkingQuoteVisitId] = useState(null);
  // Quote-request visits rendered alongside assessment requests in the
  // Appointments & Requests section — each visit appears exactly once there.
  // An appointment reads as "passed" once its window END is behind us — or,
  // for point-in-time entries, start + 4h (a visit isn't over the minute it
  // begins). Entries are NEVER dropped while the request is open: a visit
  // that happened is the owner's record that it happened. A quote_amount by
  // itself doesn't mean the visit is old news — it can be a stale pre-visit
  // price (the forced-amount era) — so "quotation is in" is only claimed
  // when the latest offer LANDED AFTER the visit started.
  const quoteVisitEntries = useMemo(() => {
    const GRACE_MS = 4 * 60 * 60 * 1000;
    return quoteVisits.map((qr) => {
      const windows = Array.isArray(qr.proposed_visit_windows)
        ? qr.proposed_visit_windows
        : (() => { try { return JSON.parse(qr.proposed_visit_windows || '[]'); } catch { return []; } })();
      const match = windows.find((w) => new Date(windowStart(w)).getTime() === new Date(qr.accepted_visit_window).getTime());
      const start = new Date(windowStart(match || qr.accepted_visit_window)).getTime();
      const end = match?.end ? new Date(match.end).getTime() : null;
      const passed = end ? Date.now() > end : Date.now() > start + GRACE_MS;
      const completed = Boolean(qr.visit_completed_at);
      const quotedAfterVisit = qr.quote_amount != null
        && qr.latest_offer_at
        && new Date(qr.latest_offer_at).getTime() > start;
      return {
        qr,
        isCall: qr.visit_type === 'client_call',
        when: formatHarareWindow(match || qr.accepted_visit_window),
        scheduledAt: start,
        passed,
        completed,
        completedBy: qr.visit_completed_by,
        quotedAfterVisit,
        photoCount: Array.isArray(qr.visit_photo_urls) ? qr.visit_photo_urls.length : 0,
        who: (qr.contractor_display_name || qr.verifier_display_name || '').trim() || 'The contractor',
        rescheduledCount: Number(qr.visit_rescheduled_count) || 0,
        contractorId: qr.contractor_id || qr.verifier_id
      };
    });
  }, [quoteVisits]);

  // One quote-request visit entry in the Appointments & Requests section —
  // same look as the assessment entries around it.
  const renderQuoteVisitEntry = (a) => (
    <Paper key={`qv-${a.qr.id}`} elevation={2} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${border.default}` }}>
      {/* One color per state: green = upcoming, orange = needs your action
          (time passed, unconfirmed), blue = completed record. */}
      <Alert severity={a.completed ? 'info' : a.passed ? 'warning' : 'success'} icon={<CalendarToday fontSize="inherit" />} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {a.isCall ? 'Client call' : 'Site visit'} {a.completed ? 'completed' : a.passed ? '' : 'scheduled'} — {a.when}
          {a.passed && !a.completed && (
            <Chip label="Time passed" size="small" sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }} />
          )}
          {a.rescheduledCount > 0 && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              (rescheduled {a.rescheduledCount}×)
            </Typography>
          )}
        </Typography>
        <Typography variant="body2">
          {a.completed
            ? a.completedBy === 'client'
              ? `You confirmed this ${a.isCall ? 'call' : 'visit'} took place — ${a.who} can now price the work.${a.quotedAfterVisit ? ' Their quotation is in — review it from your quote request.' : ' Expect their quotation next.'}`
              : `${a.who} completed this ${a.isCall ? 'call' : 'visit'}${a.photoCount > 0 ? ` and shared ${a.photoCount} photo${a.photoCount === 1 ? '' : 's'} from the site` : ''} — they can now price the work.${a.quotedAfterVisit ? ' Their quotation is in — review it from your quote request.' : ' Expect their quotation next.'}`
            : a.passed
              ? `This time has passed — did the ${a.isCall ? 'call' : 'visit'} happen? Confirm it below, or wait for ${a.who} to confirm with site photos.${a.quotedAfterVisit ? ' Their quotation has since arrived — review it from your quote request.' : ''}`
              : a.isCall
                ? `${a.who} will call you at this time. No further action needed on times.`
                : `${a.who} will visit the property at this time. No further action needed on visit times.`}
        </Typography>
        {!a.completed && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {a.passed && (
              <Button
                size="small"
                variant="contained"
                disabled={markingQuoteVisitId === a.qr.id}
                onClick={() => handleMarkQuoteVisitDone(a.qr)}
                sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
              >
                {markingQuoteVisitId === a.qr.id ? 'Saving…' : `Yes, the ${a.isCall ? 'call' : 'visit'} happened`}
              </Button>
            )}
            <Button size="small" variant="outlined" color={a.passed ? 'primary' : 'success'} onClick={() => handleOpenQuoteReschedule(a.qr)}>
              {a.passed ? "Didn't happen? Reschedule" : 'Reschedule Visit'}
            </Button>
          </Box>
        )}
      </Alert>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{a.who}</Typography>
          <Typography variant="caption" color="text.secondary">
            {a.quotedAfterVisit
              ? 'From your quote request — their quotation is waiting there.'
              : 'From your quote request — their quotation arrives there once priced.'}
          </Typography>
        </Box>
        <Stack direction="column" spacing={1} sx={{ minWidth: 190 }}>
          {a.contractorId && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Message />}
              onClick={() => navigate(`/messages?userId=${a.contractorId}&projectId=${id}`)}
            >
              Message Contractor
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<Visibility />}
            onClick={() => navigate(`/my-quote-requests?request=${a.qr.id}`)}
          >
            View Quote Request
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
  const [createMilestoneDialogOpen, setCreateMilestoneDialogOpen] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [projectFiles, setProjectFiles] = useState({ photos: [], videos: [], documents: [] });
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [findContractorsDialogOpen, setFindContractorsDialogOpen] = useState(false);
  const [findVerifierDialogOpen, setFindVerifierDialogOpen] = useState(false);
  const [escrowSetupDialogOpen, setEscrowSetupDialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [selectedMilestoneForVerification, setSelectedMilestoneForVerification] = useState(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [findContractorsInitialTab, setFindContractorsInitialTab] = useState(0);
  const [escrowSkipTerms, setEscrowSkipTerms] = useState(false);
  const [showExpenditureBreakdown, setShowExpenditureBreakdown] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, body, onConfirm, confirmLabel, confirmColor }
  const [requestingBoq, setRequestingBoq] = useState(false);
  const [briefNoteDialogOpen, setBriefNoteDialogOpen] = useState(false);
  const [briefNote, setBriefNote] = useState('');
  const [savingBriefNote, setSavingBriefNote] = useState(false);
  const contractorDiscoveryRef = useRef(null);
  const proposalsSectionRef = useRef(null);
  const assessmentSectionRef = useRef(null);
  const milestonesSectionRef = useRef(null);
  const changeOrdersSectionRef = useRef(null);

  // "Where things stand" hub — one next action + timeline + quotations +
  // thread, so every notification can land on this page and make sense.
  const [hub, setHub] = useState(null);
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [hubLoading, setHubLoading] = useState(true);
  const [highlightBidId, setHighlightBidId] = useState(null);
  const loadHub = async () => {
    try {
      const res = await api.get(`/projects/${id}/hub`);
      setHub(res.data?.data || null);
    } catch (_) {
      setHub(null);
    } finally {
      setHubLoading(false);
    }
  };
  // Re-derive whenever the project reloads (funding confirmed, stage
  // approved, quotation accepted…) so the card never lags the page.
  useEffect(() => { loadHub(); }, [id, project]); // eslint-disable-line react-hooks/exhaustive-deps
  const scrollToSection = (key) => {
    const map = {
      milestones: milestonesSectionRef, 'find-contractors': contractorDiscoveryRef,
      'change-orders': changeOrdersSectionRef, proposals: proposalsSectionRef,
      'assessment-requests': assessmentSectionRef,
    };
    const el = map[key]?.current || document.getElementById(`project-hub-${key}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleHubAction = ({ action, scrollTo, reload }) => {
    if (reload) { loadHub(); return; }
    // Terms were accepted when the wallet was first set up — a client topping
    // up for stage 2 shouldn't have to re-read them.
    if (action === 'fund') { setEscrowSkipTerms(Boolean(hub?.funded)); setEscrowSetupDialogOpen(true); return; }
    if (action === 'outcome') { setOutcomeDialogOpen(true); return; }
    if (scrollTo) scrollToSection(scrollTo);
  };

  useEffect(() => {
    loadProjectData();
    loadProjectFiles();
  }, [id]);

  // Detect wallet funding result from Stripe redirect, or action=fund from dashboard CTA
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('wallet_funded') === 'true') {
      const sessionId = params.get('session_id');
      navigate(`/projects/${id}`, { replace: true });

      if (!sessionId) {
        setError('Stripe returned without a checkout session. Refresh in a moment or contact support if the wallet is not funded.');
        return;
      }

      setSuccess('Confirming wallet funding...');
      escrowService.confirmStripeCheckout(sessionId)
        .then((result) => {
          if (result.status === 'paid') {
            setSuccess(milestones.length > 0
              ? 'Wallet funded! Your contractor has been notified and can begin work. You\'ll be asked to review each stage before anything is released.'
              : 'Wallet funded! Your contractor has been notified. Next: agree the work stages with them — each stage is released only when you approve it.');
            loadProjectData();
          } else {
            setSuccess('');
            setError('Stripe has not confirmed the wallet funding yet. Refresh in a moment.');
          }
        })
        .catch((err) => {
          setSuccess('');
          setError(err?.response?.data?.error || err.message || 'Failed to confirm wallet funding.');
        });
    } else if (params.get('wallet_cancelled') === 'true') {
      setError('Wallet funding was cancelled.');
      navigate(`/projects/${id}`, { replace: true });
    } else if (params.get('action') === 'fund') {
      // Opened from dashboard "Fund Wallet" CTA — auto-open escrow setup
      setEscrowSkipTerms(false);
      setEscrowSetupDialogOpen(true);
      navigate(`/projects/${id}`, { replace: true });
    } else if (params.get('tab') === 'proposals') {
      // Scroll to proposals section after data loads
      setTimeout(() => {
        proposalsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      navigate(`/projects/${id}`, { replace: true });
    } else if (params.get('tab') === 'assessment-requests') {
      if (params.get('visitFeedback')) setVisitFeedbackRequestId(params.get('visitFeedback'));
      setTimeout(() => {
        assessmentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      navigate(`/projects/${id}`, { replace: true });
    } else if (params.get('outcome') === '1') {
      // 30/60-day check-in email: land straight in the one-tap outcome dialog.
      setTimeout(() => setOutcomeDialogOpen(true), 600);
      navigate(`/projects/${id}`, { replace: true });
    } else if (params.get('tab') === 'quotations' || params.get('tab') === 'messages') {
      // Notification deep-links land on the hub card at the top of the page;
      // highlight the quotation they came about, if any.
      if (params.get('bid')) setHighlightBidId(params.get('bid'));
      const target = params.get('tab');
      setTimeout(() => document.getElementById(`project-hub-${target}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 800);
      navigate(`/projects/${id}`, { replace: true });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to real-time project and milestone updates
  useEffect(() => {
    if (!id) return;
    notificationService.joinProject(id);
    const unsubProject = notificationService.on('project-updated', (data) => {
      if (data.projectId === id || data.project_id === id) {
        loadProjectData();
      }
    });
    const unsubMilestone = notificationService.on('milestone-updated', (data) => {
      if (data.projectId === id || data.project_id === id) {
        loadProjectData();
      }
    });
    return () => {
      unsubProject();
      unsubMilestone();
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectFiles = async () => {
    try {
      setFilesLoading(true);
      const response = await api.get(`/projects/${id}/files`);

      if (response.data.success) {
        setProjectFiles({
          photos: response.data.photos || [],
          videos: response.data.videos || [],
          documents: response.data.documents || []
        });
      }
    } catch (err) {
      console.error('Error loading project files:', err);
      // Don't show error to user, just log it
    } finally {
      setFilesLoading(false);
    }
  };

  // Delete a reference file (project_files only — the backend additionally
  // restricts deletion to files the current user uploaded).
  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.fileName || 'this file'}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${id}/files/${file.id}`);
      await loadProjectFiles();
    } catch (err) {
      console.error('Error deleting file:', err);
      alert(err.response?.data?.error || 'Failed to delete the file. Please try again.');
    }
  };

  // Helper function to format dates safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Not specified' : date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Visit times are shown in the property's timezone (Harare / CAT) with a
  // label, so diaspora clients and on-the-ground contractors see the same time.
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not specified';
    return formatHarare(dateString) || 'Not specified';
  };

  // Helper function to format currency
  const formatCurrency = (amount, currency = 'USD') => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 300) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const safeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return value.startsWith('{') && value.endsWith('}')
          ? value.slice(1, -1).split(',').filter(Boolean)
          : [];
      }
    }
    return [];
  };

  // datetime-local pickers for visits are anchored to Harare time, not the
  // viewer's local zone, so what the owner picks is what the contractor gets.
  const toDateTimeInputValue = (dateString) => isoToHarareInput(dateString);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load project details (single-project endpoint returns contractor, milestones, escrow)
      const foundProject = await projectService.getProject(id);

      if (!foundProject) {
        setError('Project not found');
        return;
      }

      setProject(foundProject);

      // Use milestones from the single-project response if available, otherwise fetch separately
      if (foundProject.milestones) {
        setMilestones(foundProject.milestones);
      } else {
        try {
          const milestonesData = await milestoneService.getMilestones(id);
          setMilestones(milestonesData || []);
        } catch (err) {
          console.error('Error loading milestones:', err);
          setMilestones([]);
        }
      }

      // Load proposals if user is client
      if (user?.role === 'client') {
        try {
          const proposalsData = await proposalService.getProposalsForProject(id);
          setProposals(proposalsData.data || []);
        } catch (err) {
          console.error('Error loading proposals:', err);
          setProposals([]);
        }

        await loadAssessmentRequests();

        // Visits confirmed on QUOTE REQUESTS tied to this project live in a
        // different table than assessment visits — surface them here too so
        // the project page shows every appointment, whichever flow made it.
        try {
          const qrData = await api.get('/quote-requests');
          const confirmed = (qrData.data?.data?.quoteRequests || []).filter((qr) =>
            String(qr.project_id) === String(id)
            && qr.accepted_visit_window
            && ['pending', 'responded', 'negotiating'].includes(qr.status));
          setQuoteVisits(confirmed);
        } catch (err) {
          console.error('Error loading quote-request visits:', err);
          setQuoteVisits([]);
        }
      }

      // Build milestone timer map from milestone data directly (auto_release_at is on the milestones table)
      if (user?.role === 'client' || user?.role === 'contractor') {
        const loadedMilestones = foundProject.milestones || [];
        const timerMap = {};
        loadedMilestones.forEach(m => {
          if (m.status === 'completed' && m.auto_release_at) {
            timerMap[m.id] = {
              autoReleaseAt: m.auto_release_at,
              verifierHired: m.verifier_hired,
            };
          }
        });
        setMilestoneTimers(timerMap);
      }
    } catch (err) {
      console.error('Error loading project:', err);
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const openAssessmentResponseDialog = (request) => {
    setSelectedAssessmentRequest(request);
    const existingProposedWindows = safeArray(request.owner_proposed_windows)
      .map(toDateTimeInputValue)
      .filter(Boolean);
    setAssessmentResponse({
      responseMessage: request.owner_response_message || '',
      providedItems: safeArray(request.owner_provided_items),
      acceptedWindow: request.accepted_window ? toDateTimeInputValue(request.accepted_window) : '',
      proposedWindows: [...existingProposedWindows, '', '', ''].slice(0, 3),
      decline: false
    });
    setAssessmentResponseDialogOpen(true);
  };

  const toggleProvidedItem = (item) => {
    setAssessmentResponse((prev) => ({
      ...prev,
      providedItems: prev.providedItems.includes(item)
        ? prev.providedItems.filter((value) => value !== item)
        : [...prev.providedItems, item]
    }));
  };

  const isAssessmentSiteVisitDue = (request) => {
    if (!request?.site_visit_id || !request?.scheduled_site_visit_date) return false;
    if (request?.scheduled_site_visit_status === 'completed') return false;
    return new Date(request.scheduled_site_visit_date).getTime() <= Date.now();
  };

  const handleCompleteAssessmentSiteVisit = async (request) => {
    const confirmed = window.confirm('Mark this site visit as completed? The contractor will be able to use it as pricing evidence.');
    if (!confirmed) return;

    try {
      setError('');
      await api.patch(`/site-visits/${request.site_visit_id}/complete`);
      setSuccess('Site visit marked complete.');
      loadProjectData();
    } catch (err) {
      console.error('Error completing site visit:', err);
      setError(err.response?.data?.message || 'Failed to mark site visit complete');
    }
  };

  // A scheduled assessment time may be a physical site visit or a client
  // call — label by the visit record's type, never assume "site visit".
  const isCallVisit = (request) => ['client_call', 'video_call'].includes(request?.scheduled_site_visit_type);
  const visitLabelFor = (request) => (isCallVisit(request) ? 'Client call' : 'Site visit');

  // Friendly labels for assessment-request statuses — never leak raw status
  // keys ("ready_for_verified_bid") into the UI; the market's word is quotation.
  const formatAssessmentStatus = (request) => ({
    submitted: 'Awaiting your response',
    owner_responded: 'You responded',
    details_provided: 'Details provided',
    site_visit_scheduled: `${visitLabelFor(request)} scheduled`,
    ready_for_verified_bid: 'Ready for quotation',
    declined: 'Declined',
    withdrawn: 'Withdrawn'
  }[request?.status] || (request?.status ? request.status.replaceAll('_', ' ') : 'Submitted'));

  // One-step reschedule: the parties re-agree in chat when reality intervenes;
  // the owner records the new time and the contractor is notified. Works for
  // both appointment sources — assessment visits and quote-request visits.
  const handleOpenReschedule = (request) => {
    setRescheduleError('');
    setRescheduleQuoteId(null);
    setRescheduleTime(isoToHarareInput(request.scheduled_site_visit_date) || '');
    setRescheduleRequest(request);
  };

  const handleOpenQuoteReschedule = (qr) => {
    setRescheduleError('');
    setRescheduleQuoteId(qr.id);
    setRescheduleTime(isoToHarareInput(qr.accepted_visit_window) || '');
    setRescheduleRequest(qr); // opens the shared dialog
  };

  // Owner attests a passed quote-request visit took place — no proof needed
  // (they were at the property); the contractor is nudged for the quotation.
  const handleMarkQuoteVisitDone = async (qr) => {
    setMarkingQuoteVisitId(qr.id);
    try {
      await api.post(`/quote-requests/${qr.id}/complete-visit`);
      await loadProjectData();
    } catch (err) {
      console.error('Mark quote visit done error:', err);
    } finally {
      setMarkingQuoteVisitId(null);
    }
  };

  const handleSubmitReschedule = async () => {
    if (!rescheduleTime) {
      setRescheduleError('Pick the new visit date and time.');
      return;
    }
    setRescheduling(true);
    setRescheduleError('');
    try {
      const response = rescheduleQuoteId
        ? await api.put(`/quote-requests/${rescheduleQuoteId}/reschedule-visit`, { newTime: harareInputToIso(rescheduleTime) })
        : await api.put(`/projects/${id}/assessment-requests/${rescheduleRequest.id}/reschedule-visit`, { newTime: harareInputToIso(rescheduleTime) });
      if (response.data.success) {
        setSuccess(response.data.message || 'Rescheduled — the contractor has been notified.');
        setRescheduleRequest(null);
        setRescheduleQuoteId(null);
        loadProjectData();
      }
    } catch (err) {
      setRescheduleError(err.response?.data?.message || 'Failed to reschedule the visit.');
    } finally {
      setRescheduling(false);
    }
  };

  const handleRespondToAssessmentRequest = async () => {
    if (!selectedAssessmentRequest) return;

    try {
      setError('');
      // A value picked from the contractor's suggested windows is already a
      // full ISO (has 'Z'); a value typed into a datetime-local picker is a
      // zoneless Harare wall-clock string that must be anchored to CAT.
      const toIso = (v) => (typeof v === 'string' && (v.includes('Z') || v.length > 16) ? v : harareInputToIso(v));
      const payload = {
        responseMessage: assessmentResponse.responseMessage,
        providedItems: assessmentResponse.providedItems,
        acceptedWindow: assessmentResponse.acceptedWindow ? toIso(assessmentResponse.acceptedWindow) : undefined,
        proposedWindows: assessmentResponse.proposedWindows.filter(Boolean).map(toIso),
        decline: assessmentResponse.decline,
        declineReason: assessmentResponse.decline ? (assessmentResponse.declineReason || undefined) : undefined
      };

      await api.put(
        `/projects/${id}/assessment-requests/${selectedAssessmentRequest.id}/respond`,
        payload
      );

      setSuccess('Assessment response sent to the contractor.');
      setAssessmentResponseDialogOpen(false);
      setSelectedAssessmentRequest(null);
      loadProjectData();
    } catch (err) {
      console.error('Error responding to assessment request:', err);
      setError(err.response?.data?.message || 'Failed to respond to assessment request');
    }
  };

  const handleGenerateMilestones = async () => {
    try {
      setGeneratingMilestones(true);
      setError('');
      setSuccess('');
      
      // Validate project has enough info
      if (!project.description || project.description.length < 50) {
        setError('Project description is too short. Please add more details to generate meaningful milestones.');
        return;
      }
      
      const result = await aiService.generateMilestones({
        projectType: project.projectType || 'full',
        title: project.title,
        description: project.description,
        budget: project.budget,
      });
      
      if (!result || !result.success) {
        throw new Error(result?.error || 'Failed to generate milestones');
      }
      
      if (!result.data?.milestones || result.data.milestones.length === 0) {
        throw new Error('No milestones were generated. Please try again or create them manually.');
      }
      
      // Save generated milestones
      const savePromises = result.data.milestones.map(milestone =>
        milestoneService.createMilestone(id, milestone)
      );
      
      await Promise.all(savePromises);
      
      // Reload project data
      await loadProjectData();
      
      // Show success message
      setSuccess(`Successfully generated ${result.data.milestones.length} milestones!`);
      
    } catch (err) {
      console.error('Error generating milestones:', err);
      
      // User-friendly error messages
      let errorMessage = 'Failed to generate milestones. ';
      
      if (err.message?.includes('API key')) {
        errorMessage += 'AI service is not configured. Please contact support.';
      } else if (err.message?.includes('network') || err.message?.includes('timeout')) {
        errorMessage += 'Network error. Please check your connection and try again.';
      } else {
        errorMessage += err.message || 'Please try again or create milestones manually.';
      }
      
      setError(errorMessage);
    } finally {
      setGeneratingMilestones(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Draft', bgColor: '#9E9E9E' },
      open_for_bids: { label: 'Open for Quotations', bgColor: '#4CAF50' },
      in_review: { label: 'In Review', bgColor: '#FF9800' },
      in_progress: { label: 'In Progress', bgColor: '#2196F3' },
      completed: { label: 'Completed', bgColor: '#9C27B0' },
      on_hold: { label: 'On Hold', bgColor: '#F44336' }
    };
    return configs[status] || configs.draft;
  };

  const getMilestoneStatusColor = (status) => {
    const colors = {
      pending: '#9E9E9E',
      not_started: '#9E9E9E',
      in_progress: '#2196F3',
      completed: '#4CAF50',
      verified: '#9C27B0',
      failed: '#F44336',
      delayed: '#F44336',
      pending_approval: '#FF9800'
    };
    return colors[status] || colors.pending;
  };

  const getMilestoneStatusLabel = (status) => {
    const labels = {
      not_started: 'Not Started',
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
      verified: 'Verified',
      failed: 'Failed',
      delayed: 'Delayed',
      pending_approval: 'Pending Approval'
    };
    return labels[status] || status;
  };

  const handleOpenFundWallet = (skipTerms = false) => {
    setEscrowSkipTerms(skipTerms);
    setEscrowSetupDialogOpen(true);
  };

  const handleReleasePayment = (milestone) => {
    const amount = formatCurrency(parseFloat(milestone.budget_allocation) || 0, project.currency);
    setConfirmDialog({
      title: 'Release Payment',
      body: `Release ${amount} for "${milestone.title || milestone.name}"? This action cannot be undone.`,
      confirmLabel: 'Release Payment',
      confirmColor: 'success',
      onConfirm: async () => {
        try {
          await api.post(`/escrow/milestone/${milestone.id}/confirm-payment`, {
            paymentGateway: 'stripe',
            termsAccepted: true,
            breakdownAcknowledged: true,
            contractorPaymentDetails: { method: 'platform_balance' },
          });
          setSuccess(`Payment released for ${milestone.title || milestone.name}`);
          loadProjectData();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to release payment');
        }
      }
    });
  };

  const handleApproveMilestone = (milestone) => {
    const amount = formatCurrency(parseFloat(milestone.budget_allocation) || 0, project.currency);
    const hasNoVerifier = !milestone.verifier_id;
    setConfirmDialog({
      title: 'Approve Milestone',
      body: `Approve and release ${amount} for "${milestone.title || milestone.name}"?${hasNoVerifier ? '\n\nNo verifier is assigned to this milestone. A professional verifier can review the work first and give you confidence before payment is released. Are you sure you want to approve without verification?' : ''}`,
      confirmLabel: 'Approve',
      confirmColor: hasNoVerifier ? 'warning' : 'success',
      onConfirm: async () => {
        try {
          await api.put(`/milestones/${milestone.id}/approve`);
          setSuccess(`Milestone "${milestone.title || milestone.name}" approved`);
          loadProjectData();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to approve milestone');
        }
      }
    });
  };

  // Countdown timer component for milestone auto-release
  const CountdownTimer = ({ autoReleaseAt }) => {
    const [display, setDisplay] = useState('');
    const [urgencyColor, setUrgencyColor] = useState('#e65100');

    useEffect(() => {
      const update = () => {
        const diff = new Date(autoReleaseAt) - Date.now();
        if (diff <= 0) {
          setDisplay('Auto-releasing...');
          setUrgencyColor('#d32f2f');
          return;
        }
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        if (hours >= 24) {
          const days = Math.floor(hours / 24);
          setDisplay(`${days}d ${hours % 24}h ${minutes}m`);
          setUrgencyColor('#e65100');
        } else if (hours >= 1) {
          setDisplay(`${hours}h ${minutes}m ${seconds}s`);
          setUrgencyColor(hours <= 6 ? '#d32f2f' : '#e65100');
        } else {
          setDisplay(`${minutes}m ${seconds}s`);
          setUrgencyColor('#d32f2f');
        }
      };
      update();
      const interval = setInterval(update, 1000);
      return () => clearInterval(interval);
    }, [autoReleaseAt]);

    return (
      <Chip
        icon={<Schedule sx={{ color: '#fff !important' }} />}
        label={display}
        size="small"
        sx={{ bgcolor: urgencyColor, color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}
      />
    );
  };

  const scrollToContractorDiscovery = () => {
    contractorDiscoveryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleMessageOtherParty = async () => {
    try {
      // Determine the other party: if I'm the client, message the contractor and vice versa
      const otherId = user?.role === 'contractor'
        ? (project?.client_id || project?.clientId)
        : (project?.contractor_id || project?.contractor_id);
      if (!otherId) { navigate('/messages'); return; }
      const res = await messagingService.getOrCreateConversation(otherId, project?.id);
      const convId = res.data?.id;
      navigate(convId ? `/messages?conversation=${convId}` : '/messages');
    } catch {
      navigate('/messages');
    }
  };

  const handleRequestMaterialsSchedule = async () => {
    if (!project?.contractor_id && !project?.contractor?.id) {
      setError('Assign a contractor before requesting a materials schedule.');
      return;
    }

    try {
      setRequestingBoq(true);
      setError('');
      const result = await projectService.requestMaterialsSchedule(id);
      setSuccess(result.message || 'Materials schedule request sent to the contractor.');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to request materials schedule.');
    } finally {
      setRequestingBoq(false);
    }
  };

  const handleSaveBriefNote = async () => {
    if (!briefNote.trim()) {
      setError('Please enter a note before saving.');
      return;
    }

    try {
      setSavingBriefNote(true);
      setError('');
      const timestamp = new Date().toLocaleDateString();
      const currentDescription = project?.description || '';
      const nextDescription = [
        currentDescription.trim(),
        `Owner note (${timestamp}):\n${briefNote.trim()}`
      ].filter(Boolean).join('\n\n');

      await projectService.updateProject(id, { description: nextDescription });
      setProject(prev => ({ ...prev, description: nextDescription }));
      setBriefNote('');
      setBriefNoteDialogOpen(false);
      setDescriptionExpanded(true);
      setSuccess('Project brief updated.');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to update project brief.');
    } finally {
      setSavingBriefNote(false);
    }
  };

  const getPrimaryAction = () => {
    if (!project) return null;
    
    if (project.status === 'draft' || project.status === 'open_for_bids') {
      return (
        <>
          <Button
            variant="contained"
            size="large"
            startIcon={<AccountBalanceWallet />}
            onClick={() => handleOpenFundWallet(false)}
            sx={{
              bgcolor: status.success,
              '&:hover': { bgcolor: '#3aa370' }
            }}
          >
            Fund Wallet
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={scrollToContractorDiscovery}
            sx={{
              borderColor: brand.primary,
              color: brand.primary,
              '&:hover': { bgcolor: brand.primary, color: '#fff' }
            }}
          >
            Find Contractors
          </Button>
        </>
      );
    }
    
    if (project.status === 'in_progress') {
      return (
        <Button
          variant="contained"
          size="large"
          startIcon={<Message />}
          onClick={handleMessageOtherParty}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            }
          }}
        >
          Message Contractor
        </Button>
      );
    }

    if (project.status === 'completed') {
      return (
        <Button
          variant="contained"
          size="large"
          startIcon={<CheckCircle />}
          onClick={() => navigate(`/projects/${id}`)}
          sx={{
            background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8E24AA 0%, #6A1B9A 100%)'
            }
          }}
        >
          Project Completed
        </Button>
      );
    }

    if (project.status === 'on_hold') {
      return (
        <Button
          variant="contained"
          size="large"
          startIcon={<Message />}
          onClick={handleMessageOtherParty}
          sx={{
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #FB8C00 0%, #EF6C00 100%)'
            }
          }}
        >
          Message Contractor
        </Button>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading project...</Typography>
      </Container>
    );
  }

  if (error && !project) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/projects')}>
          Back to Projects
        </Button>
      </Container>
    );
  }

  const statusConfig = getStatusConfig(project.status);
  const walletBalance = parseFloat(project?.escrow?.total_amount || 0);
  const spent = milestones
    .filter(m => ['completed', 'verified', 'released'].includes(m.status))
    .reduce((sum, m) => sum + (parseFloat(m.budget_allocation) || 0), 0);
  const spentPercent = walletBalance > 0 ? Math.round((spent / walletBalance) * 100) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          Back to Projects
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {project.title}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Project ID: {project.id}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            {getPrimaryAction()}
          </Stack>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Where things stand — one next action, timeline, quotations, thread */}
      {(user?.role === 'client' || (user?.role === 'contractor' && project?.contractor_id === user?.id)) && (
        <ProjectHubCard
          hub={hub}
          loading={hubLoading}
          currentUserId={user?.id}
          projectId={id}
          onAction={handleHubAction}
          highlightBidId={highlightBidId}
        />
      )}

      {/* Project Brief & Reference Files */}
      {user?.role === 'client' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Project Brief & Reference Photos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep site photos, inspiration images, plans, and notes in one place so contractors can quote from what you mean, not construction jargon.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{ borderColor: brand.primary, color: brand.primary, flexShrink: 0 }}
              >
                Upload Files
              </Button>
            </Box>

            {filesLoading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : projectFiles.photos.length > 0 || projectFiles.documents.length > 0 ? (
              <Stack spacing={2}>
                {projectFiles.photos.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Photos
                    </Typography>
                    <Grid container spacing={1.5}>
                      {(showAllPhotos ? projectFiles.photos : projectFiles.photos.slice(0, 8)).map((photo, index) => (
                        <Grid item xs={6} sm={4} md={3} key={photo.id || index}>
                          <Paper
                            variant="outlined"
                            sx={{
                              position: 'relative',
                              aspectRatio: '1 / 1',
                              overflow: 'hidden',
                              borderRadius: 1.5,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSelectedMedia(photo);
                              setMediaViewerOpen(true);
                            }}
                          >
                            <Box
                              component="img"
                              src={photo.url}
                              alt={photo.description || photo.fileName || 'Project reference photo'}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            {photo.source === 'project_file' && user?.id === project?.owner_id && (
                              <IconButton
                                size="small"
                                aria-label="Delete photo"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile(photo);
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'rgba(0,0,0,0.55)',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'rgba(211,47,47,0.85)' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    {projectFiles.photos.length > 8 && !showAllPhotos && (
                      <Button
                        size="small"
                        sx={{ mt: 1, color: brand.primary }}
                        onClick={() => setShowAllPhotos(true)}
                      >
                        View all {projectFiles.photos.length} photos
                      </Button>
                    )}
                  </Box>
                )}

                {projectFiles.documents.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Documents
                    </Typography>
                    <Stack spacing={1}>
                      {projectFiles.documents.slice(0, 4).map((doc, index) => (
                        <Paper
                          key={doc.id || index}
                          variant="outlined"
                          sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}
                        >
                          <Description color="primary" />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {doc.fileName || 'Document'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Uploaded {formatDate(doc.uploadedAt)} by {doc.uploadedBy}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => window.open(doc.url, '_blank')}>
                            <Download fontSize="small" />
                          </IconButton>
                          {doc.source === 'project_file' && user?.id === project?.owner_id && (
                            <IconButton
                              size="small"
                              aria-label="Delete document"
                              onClick={() => handleDeleteFile(doc)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            ) : (
              <Paper
                variant="outlined"
                sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderStyle: 'dashed' }}
              >
                <Photo sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  No reference files yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload photos or plans before talking to contractors so everyone is looking at the same thing.
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {/* Materials Schedule / BOQ — Prominent CTA */}
      {(() => {
        const isContractorHere = user?.role === 'contractor' && project?.contractor_id === user?.id;
        const isProjectManagerHere = user?.role === 'project_manager' && project?.project_manager_id === user?.id;
        const canManageBoq = isContractorHere || isProjectManagerHere;
        const hasContractor = Boolean(project?.contractor_id || project?.contractor);
        if (!canManageBoq && !(user?.role === 'client' && hasContractor)) return null;
        return (
      <Paper sx={{
        mb: 3, p: 2.5, borderRadius: 3,
        background: `linear-gradient(135deg, ${brand.primaryDeep}08 0%, ${brand.primary}06 100%)`,
        border: `1px solid ${brand.primary}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DescriptionOutlinedIcon sx={{ color: brand.primary, fontSize: 32 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: brand.primaryDark }}>
              {canManageBoq ? 'Materials Schedule (BOQ)' : 'Request Materials Schedule (BOQ)'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {canManageBoq
                ? 'Prepare and manage the Materials Schedule. List materials, quantities, unit costs, and labour — submit for client sign-off before procurement.'
                : 'Ask your assigned contractor to prepare quantities, labor, and material costs for your review before procurement.'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canManageBoq ? (
            <>
              <Button
                variant="outlined"
                onClick={() => navigate(`/projects/${id}/boq`)}
                sx={{
                  borderColor: brand.primary, color: brand.primary,
                  borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600
                }}
              >
                My Schedules
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate(`/projects/${id}/boq/new`)}
                sx={{
                  bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark },
                  borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600
                }}
              >
                New Materials Schedule
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={handleRequestMaterialsSchedule}
              disabled={requestingBoq}
              sx={{
                bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark },
                borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600
              }}
            >
              {requestingBoq ? 'Sending...' : 'Request Schedule'}
            </Button>
          )}
        </Box>
      </Paper>
        );
      })()}

      {/* Project Overview Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Project Overview
          </Typography>
          
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}
          >
            {descriptionExpanded 
              ? project.description 
              : truncateText(project.description, 300)
            }
          </Typography>
          
          {project.description && project.description.length > 300 && (
            <Button 
              size="small" 
              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              sx={{ mb: 2 }}
            >
              {descriptionExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
          
          <Divider sx={{ my: 2 }} />

          {/* Project health indicator */}
          {milestones.length > 0 && (() => {
            const completed = milestones.filter(m => ['completed', 'approved', 'paid'].includes(m.status)).length;
            const overdue = milestones.filter(m => m.due_date && new Date(m.due_date) < new Date() && !['completed', 'approved', 'paid'].includes(m.status)).length;
            const healthLabel = overdue > 0 ? 'At Risk' : completed === milestones.length ? 'Completed' : completed > 0 ? 'On Track' : 'Getting Started';
            const healthColor = overdue > 0 ? 'error.main' : completed === milestones.length ? 'success.main' : 'warning.main';
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: healthColor, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{healthLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  — {completed}/{milestones.length} milestones done{overdue > 0 ? `, ${overdue} overdue` : ''}
                </Typography>
              </Box>
            );
          })()}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {project.location || 'Location not specified'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {project.contractor
                    ? `${project.contractor.first_name} ${project.contractor.last_name}`
                    : 'No Contractor Assigned'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Created {formatDate(project.created_at)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Project Wallet Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Project Wallet
                </Typography>
              </Box>
              {project.budget > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5 }}>
                  Project Budget: {formatCurrency(project.budget, project.currency)}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenFundWallet(true)}
              sx={{
                background: brand.gradient,
                '&:hover': {
                  background: brand.gradientHover
                }
              }}
            >
              Add Money to Wallet
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Wallet Balance
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: walletBalance > 0 ? 'success.dark' : 'text.secondary' }}>
                  {formatCurrency(walletBalance, project.currency)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Funds deposited into your wallet
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Amount Spent
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: spent > 0 ? 'warning.dark' : 'text.primary' }}>
                  {formatCurrency(spent, project.currency)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Released through completed work stages
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Wallet Used
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {spentPercent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(spentPercent, 100)}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'grey.200'
              }}
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              size="small"
              onClick={() => setShowExpenditureBreakdown(!showExpenditureBreakdown)}
              endIcon={showExpenditureBreakdown ? <ExpandLess /> : <ExpandMore />}
              sx={{ textTransform: 'none' }}
            >
              {showExpenditureBreakdown ? 'Hide' : 'View'} Expenditure Report
            </Button>
          </Box>

          <Collapse in={showExpenditureBreakdown}>
            <Divider sx={{ my: 2 }} />
            {milestones.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Milestone</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestones.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.title || m.name || 'Untitled'}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(parseFloat(m.budget_allocation) || 0, project.currency)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getMilestoneStatusLabel(m.status)}
                            size="small"
                            sx={{
                              bgcolor: getMilestoneStatusColor(m.status),
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                      <TableCell>Total</TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          milestones.reduce((sum, m) => sum + (parseFloat(m.budget_allocation) || 0), 0),
                          project.currency
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          {milestones.filter(m => ['completed', 'verified'].includes(m.status)).length}/{milestones.length} completed
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No milestones created yet. Expenditure details will appear here once milestones are set up.
              </Typography>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* Project Analytics — budget vs actual, funds flow, timeline health */}
      {user?.role === 'client' && (
        <ProjectAnalyticsPanel projectId={id} project={project} milestones={milestones} />
      )}

      {/* Change Orders — both parties propose and sign variations here */}
      {(user?.role === 'client' || user?.role === 'contractor') && project && (
        <Box ref={changeOrdersSectionRef}>
        <ChangeOrdersPanel
          projectId={id}
          project={project}
          milestones={milestones}
          user={user}
          onChanged={loadProjectData}
        />
        </Box>
      )}

      {/* ── Appointments & Requests — assessment requests AND quote-request
             visits in ONE section, so each visit appears exactly once ── */}
      {user?.role === 'client' && (assessmentRequests.length > 0 || quoteVisitEntries.length > 0) && (
        <Card ref={assessmentSectionRef} sx={{ mb: 3, border: `2px solid ${status.warning}` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FactCheck sx={{ color: status.warningDark }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    Appointments & Requests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Site visits, calls, and the details contractors need before they can give a responsible fixed price.
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${assessmentRequests.length + quoteVisitEntries.length} request${assessmentRequests.length + quoteVisitEntries.length !== 1 ? 's' : ''}`}
                sx={{ bgcolor: status.warning, color: 'white', fontWeight: 700 }}
              />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              You can upload plans/photos/documents to the project, accept a suggested visit window, or propose different times.
            </Alert>

            <Stack spacing={2}>
              {/* One list, what-needs-you-first: upcoming appointments (soonest
                  at the top), then requests with no visit time yet, then past/
                  completed visits sinking to the bottom (oldest last). */}
              {[
                ...assessmentRequests.map((request) => {
                  const at = request.scheduled_site_visit_date
                    ? new Date(request.scheduled_site_visit_date).getTime()
                    : null;
                  const done = request.scheduled_site_visit_status === 'completed' ||
                    (request.scheduled_site_visit_status === 'scheduled' && at &&
                      Date.now() > at + 4 * 60 * 60 * 1000);
                  return { kind: 'assessment', at, done, request };
                }),
                ...quoteVisitEntries.map((entry) => ({
                  kind: 'quote',
                  at: entry.scheduledAt,
                  done: entry.completed || entry.passed,
                  entry
                }))
              ].sort((x, y) => {
                const groupOf = (i) => (i.done ? 2 : i.at == null ? 1 : 0);
                const gx = groupOf(x);
                const gy = groupOf(y);
                if (gx !== gy) return gx - gy;
                if (gx === 0) return x.at - y.at; // upcoming: soonest first
                if (gx === 2) return (y.at || 0) - (x.at || 0); // past: oldest last
                return 0; // awaiting response: keep original order
              }).map((item) => {
                if (item.kind === 'quote') {
                  const a = item.entry;
                  return renderQuoteVisitEntry(a);
                }
                const { request } = item;
                const contractorName = request.contractor_company_name ||
                  `${request.contractor_first_name || ''} ${request.contractor_last_name || ''}`.trim() ||
                  'Contractor';
                const requestedItems = safeArray(request.assessment_items);
                const contractorWindows = safeArray(request.availability_windows);
                const ownerProvidedItems = safeArray(request.owner_provided_items);
                const visitScheduled = Boolean(request.scheduled_site_visit_date);
                const visitCompleted = request.scheduled_site_visit_status === 'completed';
                // Scheduled time is 4h+ behind us but not confirmed complete —
                // don't keep promising the owner a visit that already happened.
                const visitPassed = !visitCompleted &&
                  request.scheduled_site_visit_status === 'scheduled' &&
                  Date.now() > new Date(request.scheduled_site_visit_date).getTime() + 4 * 60 * 60 * 1000;
                const visitPhotos = safeArray(request.visit_photo_urls);
                const bidDueLabel = request.bid_promised_by
                  ? new Date(request.bid_promised_by).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                  : null;
                const bidOverdue = Boolean(
                  request.bid_promised_by &&
                  !request.bid_submitted_at &&
                  new Date(request.bid_promised_by).getTime() < Date.now()
                );
                return (
                  <Paper key={request.id} elevation={2} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${border.default}` }}>
                    {/* Travel money for the visit: what it is, where it sits,
                        and the reminder that nobody should ask for cash. */}
                    <CallOutFeePanel
                      source="assessment_request"
                      request={request}
                      providerName={contractorName}
                      onChanged={loadAssessmentRequests}
                    />
                    {/* Follow-up visit: the first visit is done and the
                        contractor needs another look (e.g. a second scope). */}
                    {Number(request.visit_round) > 1 && !visitScheduled && (
                      <Alert severity="info" icon={<CalendarToday fontSize="inherit" />} sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Follow-up visit requested (visit #{request.visit_round})
                        </Typography>
                        <Typography variant="body2">
                          {contractorName} already completed an earlier visit on this project and needs another one
                          {request.availability_notes ? <> — "{request.availability_notes}"</> : null}.
                          {contractorWindows.length > 0 ? ' Pick one of their proposed times below, or suggest different times.' : ''}
                        </Typography>
                      </Alert>
                    )}
                    {visitScheduled && (
                      <Alert severity={visitCompleted ? 'info' : visitPassed ? 'warning' : 'success'} icon={<CalendarToday fontSize="inherit" />} sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {visitCompleted
                            ? `${visitLabelFor(request)} completed — ${formatDateTime(request.scheduled_site_visit_date)}`
                            : `${visitLabelFor(request)}${visitPassed ? '' : ' scheduled'} — ${formatDateTime(request.scheduled_site_visit_date)}`}
                          {visitPassed && (
                            <Chip label="Time passed" size="small" sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Typography>
                        <Typography variant="body2">
                          {visitCompleted
                            ? `${contractorName} completed this ${isCallVisit(request) ? 'call' : 'visit'} and can now price the work.`
                            : visitPassed
                              ? `This time has passed — waiting for ${contractorName} to confirm the ${isCallVisit(request) ? 'call' : 'visit'} took place.`
                              : isCallVisit(request)
                                ? `${contractorName} will call you at this time. No further action needed on times.`
                                : `${contractorName} will visit the property at this time. No further action needed on visit times.`}
                        </Typography>
                        {!visitCompleted && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button size="small" variant="outlined" color={visitPassed ? 'warning' : 'success'} onClick={() => handleOpenReschedule(request)}>
                              {visitPassed ? "Didn't happen? Reschedule" : 'Reschedule Visit'}
                            </Button>
                            {Number(request.scheduled_site_visit_rescheduled_count) > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                (rescheduled {request.scheduled_site_visit_rescheduled_count}×)
                              </Typography>
                            )}
                          </Box>
                        )}
                        {visitCompleted && visitPhotos.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              Photos {contractorName} took at the site — click to view full size:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {visitPhotos.map((photoUrl, photoIndex) => (
                                <Box
                                  key={photoUrl}
                                  component="a"
                                  href={photoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Box
                                    component="img"
                                    src={photoUrl}
                                    alt={`Site visit photo ${photoIndex + 1}`}
                                    sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'info.light', display: 'block' }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Alert>
                    )}
                    {visitCompleted && request.bid_submitted_at && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Quotation received.</strong> {contractorName} sent their quotation — review it in the Contractor Quotations section below.
                      </Alert>
                    )}
                    {visitCompleted && !request.bid_submitted_at && bidDueLabel && !bidOverdue && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>{contractorName} is preparing their quotation</strong> — they committed to sending it by {bidDueLabel}. You'll be notified when it arrives.
                      </Alert>
                    )}
                    {visitCompleted && bidOverdue && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Quotation overdue.</strong> {contractorName} promised their quotation by {bidDueLabel} and it hasn't arrived. We've reminded them automatically — you can also message them for an update.
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 260 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {contractorName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                          {request.request_message}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                          {requestedItems.map((item) => (
                            <Chip key={item} size="small" label={ASSESSMENT_LABELS[item] || item} variant="outlined" />
                          ))}
                        </Box>
                        {contractorWindows.length > 0 && !visitScheduled && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Suggested site visit windows</Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                              {contractorWindows.map((windowValue) => (
                                <Chip key={windowValue} size="small" icon={<CalendarToday />} label={formatDateTime(windowValue)} />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {ownerProvidedItems.length > 0 && (
                          <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                            Provided: {ownerProvidedItems.map((item) => ASSESSMENT_LABELS[item] || item).join(', ')}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: 180, textAlign: { xs: 'left', md: 'right' } }}>
                        <Chip
                          label={formatAssessmentStatus(request)}
                          color={
                            visitCompleted || request.status === 'ready_for_verified_bid' ? 'info'
                              : visitScheduled && !visitPassed ? 'success'
                                : 'warning'
                          }
                          sx={{ mb: 1 }}
                        />
                        <Stack spacing={1}>
                          {isAssessmentSiteVisitDue(request) && (
                            <Button
                              variant="outlined"
                              color="success"
                              size="small"
                              startIcon={<CheckCircle />}
                              onClick={() => handleCompleteAssessmentSiteVisit(request)}
                            >
                              Mark Visit Complete
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloudUpload />}
                            onClick={() => setUploadDialogOpen(true)}
                          >
                            Upload Files
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<FactCheck />}
                            onClick={() => openAssessmentResponseDialog(request)}
                          >
                            Respond
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}

            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Contractor Quotations Section ────────────────────────────────── */}
      {user?.role === 'client' && proposals.length > 0 && (
        <Card ref={proposalsSectionRef} sx={{ mb: 3, border: `2px solid ${status.info}` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Gavel sx={{ color: status.info }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    Contractor Quotations
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {proposals.length} bid{proposals.length !== 1 ? 's' : ''} received — review and hire a contractor
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${proposals.length} New`}
                sx={{ bgcolor: status.info, color: 'white', fontWeight: 700 }}
              />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Review each proposal carefully. Once you accept a bid, the contractor is hired and work can begin after your wallet is funded.
            </Alert>

            {/* Quick comparison summary when multiple proposals */}
            {proposals.length > 1 && (() => {
              const amounts = proposals.map(p => parseFloat(p.total_amount || p.amount || 0)).filter(a => a > 0);
              const minAmt = amounts.length > 0 ? Math.min(...amounts) : 0;
              const maxAmt = amounts.length > 0 ? Math.max(...amounts) : 0;
              const avgAmt = amounts.length > 0 ? amounts.reduce((a, b) => a + b, 0) / amounts.length : 0;
              return (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Lowest</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      {minAmt > 0 ? formatCurrency(minAmt, project.currency) : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Average</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {avgAmt > 0 ? formatCurrency(avgAmt, project.currency) : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Highest</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.dark' }}>
                      {maxAmt > 0 ? formatCurrency(maxAmt, project.currency) : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Proposals</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{proposals.length}</Typography>
                  </Box>
                </Box>
              );
            })()}

            <Stack spacing={2}>
              {proposals.map((proposal) => {
                const contractorName = proposal.contractor_name ||
                  (proposal.contractor ? `${proposal.contractor.first_name || ''} ${proposal.contractor.last_name || ''}`.trim() : 'Unknown Contractor');
                const rating = proposal.contractor?.average_rating || proposal.contractor_rating;
                const totalAmount = parseFloat(proposal.total_amount || proposal.amount || 0);
                const duration = proposal.estimated_duration || proposal.timeline;
                return (
                  <Paper
                    key={proposal.id}
                    elevation={2}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: `1px solid ${border.default}`,
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderColor: status.info },
                      transition: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: status.info, fontSize: 14 }}>
                            {contractorName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{contractorName}</Typography>
                            {rating && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Star sx={{ fontSize: 14, color: status.warningDark }} />
                                <Typography variant="caption" sx={{ color: '#666' }}>{parseFloat(rating).toFixed(1)}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {proposal.cover_letter && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
                            {proposal.cover_letter.length > 200
                              ? `${proposal.cover_letter.substring(0, 200)}...`
                              : proposal.cover_letter}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                          {totalAmount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AttachMoney sx={{ fontSize: 16, color: status.successDark }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, color: status.successDark }}>
                                {formatCurrency(totalAmount, project.currency)}
                              </Typography>
                            </Box>
                          )}
                          {duration && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: 16, color: '#888' }} />
                              <Typography variant="body2" color="text.secondary">{duration}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedProposal(proposal);
                          setShowProposalReview(true);
                        }}
                        sx={{
                          bgcolor: status.info,
                          '&:hover': { bgcolor: '#4752c4' },
                          fontWeight: 700,
                          textTransform: 'none',
                          flexShrink: 0
                        }}
                      >
                        Review Bid
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Milestones Timeline — shown for contractors or clients with contractor assigned (clients without contractor use work packages below).
          Also shown EMPTY to the assigned contractor, so a project accepted
          without payment stages gives them the Add Milestone entry point. */}
      {(milestones.length > 0 || (user?.role === 'contractor' && project?.contractor_id === user?.id)) && (user?.role !== 'client' || project?.contractor_id) && (
        <Card sx={{ mb: 3 }} ref={milestonesSectionRef}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {user?.role === 'client' ? 'Work Packages & Contractor Plans' : 'Project Timeline & Milestones'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.role === 'client'
                    ? 'Review specialist work packages and any contractor-proposed milestones.'
                    : 'Track progress through key project phases'}
                </Typography>
              </Box>
              {user?.role === 'contractor' && (
                <Button
                  startIcon={<Add />}
                  variant={milestones.length === 0 ? 'contained' : 'outlined'}
                  onClick={() => setCreateMilestoneDialogOpen(true)}
                >
                  {milestones.length === 0 ? 'Add Payment Stage' : 'Add Milestone'}
                </Button>
              )}
            </Box>
            {milestones.length === 0 && user?.role === 'contractor' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>No payment stages yet — you can't start work or get paid without them.</strong> Start by asking the client how they want to fund the job: everything at once, or stage by stage. Then agree a breakdown sized to what they can fund at a time — a small job can be a single stage; bigger jobs should be broken down (e.g. siting &amp; foundation first, then the next stages). Add each agreed stage here with its amount: the client funds it into escrow before you start, and it's released to you when the stage is approved.
              </Alert>
            )}

            {/* Visual milestone progress bar */}
            {milestones.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  {milestones.map((m, i) => {
                    const isCompleted = m.status === 'completed' || m.status === 'approved' || m.status === 'paid';
                    const isActive = m.status === 'in_progress' || m.status === 'pending_approval';
                    return (
                      <React.Fragment key={m.id}>
                        <Tooltip title={`${m.title || `Milestone ${i + 1}`} — ${m.status?.replace(/_/g, ' ')}`}>
                          <Box sx={{
                            flex: 1, height: 8, borderRadius: 1,
                            bgcolor: isCompleted ? 'success.main' : isActive ? 'warning.main' : '#e2e8f0',
                            transition: 'background-color 0.3s',
                          }} />
                        </Tooltip>
                      </React.Fragment>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    {milestones.filter(m => ['completed', 'approved', 'paid'].includes(m.status)).length} of {milestones.length} completed
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round((milestones.filter(m => ['completed', 'approved', 'paid'].includes(m.status)).length / milestones.length) * 100)}%
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Multi-contractor guidance for clients */}
            {user?.role === 'client' && milestones.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Hire different contractors for each trade</Typography>
                <Typography variant="body2">
                  Split your project into milestones (e.g., Tiling, Plumbing, Electrical) and hire a specialist contractor for each one.
                  Add milestones below, then contractors can bid on the specific work they specialise in.
                </Typography>
              </Alert>
            )}

            <Box>
              {/* Show pending milestones for client approval */}
              {user?.role === 'client' && milestones.filter(m => m.status === 'pending_approval').map((milestone) => (
                <MilestoneApproval
                  key={milestone.id}
                  milestone={milestone}
                  onStatusChange={(milestoneId, newStatus) => {
                    setMilestones(prev => prev.map(m =>
                      m.id === milestoneId ? { ...m, status: newStatus } : m
                    ));
                  }}
                />
              ))}

              {/* Show approved/active milestones */}
              {milestones.filter(m => m.status !== 'pending_approval').map((milestone) => (
                <Box key={milestone.id} sx={{ mb: 2 }}>
                  <Paper
                    elevation={selectedMilestone === milestone.id ? 3 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '2px solid',
                      borderColor: selectedMilestone === milestone.id ? 'primary.main' : 'transparent',
                      '&:hover': {
                        elevation: 3,
                        borderColor: 'primary.light'
                      }
                    }}
                    onClick={() => setSelectedMilestone(selectedMilestone === milestone.id ? null : milestone.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: getMilestoneStatusColor(milestone.status),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}
                      >
                        {milestone.order || '—'}
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {milestone.title || milestone.name || milestone.milestoneDetails?.name || 'Untitled Milestone'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {milestone.description || milestone.milestoneDetails?.description}
                        </Typography>
                        {milestone.contractorName ? (
                          <Chip size="small" label={milestone.contractorName} color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                        ) : null}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {!milestone.contractorId && !milestone.contractorName && user?.role === 'client' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PersonAdd />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const msName = milestone.title || milestone.name || milestone.milestoneDetails?.name || '';
                              navigate(`/contractors?projectId=${encodeURIComponent(id)}&projectTitle=${encodeURIComponent(project?.title || '')}&milestoneId=${encodeURIComponent(milestone.id)}&milestoneName=${encodeURIComponent(msName)}`);
                            }}
                            sx={{ background: brand.gradient, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', '&:hover': { background: brand.gradientHover } }}
                          >
                            Find Contractor
                          </Button>
                        )}
                        {milestone.status === 'completed' && milestoneTimers[milestone.id] && (
                          <CountdownTimer autoReleaseAt={milestoneTimers[milestone.id]?.autoReleaseAt} />
                        )}
                        <Chip
                          label={getMilestoneStatusLabel(milestone.status)}
                          size="small"
                          sx={{
                            bgcolor: getMilestoneStatusColor(milestone.status),
                            color: 'white',
                          }}
                        />
                        {selectedMilestone === milestone.id ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    </Box>
                  </Paper>

                  {/* Expanded milestone details */}
                  <Collapse in={selectedMilestone === milestone.id}>
                    <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: '0 0 8px 8px', border: '1px solid #e0e0e0', borderTop: 0 }}>
                      {milestone.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {milestone.description}
                        </Typography>
                      )}

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Budget</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(parseFloat(milestone.budget_allocation) || 0, project.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Weight</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {milestone.weight || milestone.paymentPercentage || 0}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Progress</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {milestone.progress || 0}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getMilestoneStatusLabel(milestone.status)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {(milestone.planned_start_date || milestone.planned_end_date) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Timeline</Typography>
                          <Typography variant="body2">
                            {milestone.planned_start_date ? new Date(milestone.planned_start_date).toLocaleDateString() : 'TBD'}
                            {' — '}
                            {milestone.planned_end_date ? new Date(milestone.planned_end_date).toLocaleDateString() : 'TBD'}
                          </Typography>
                        </Box>
                      )}

                      {milestone.deliverables && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Deliverables</Typography>
                          <Typography variant="body2">{
                            typeof milestone.deliverables === 'string'
                              ? milestone.deliverables
                              : JSON.stringify(milestone.deliverables)
                          }</Typography>
                        </Box>
                      )}

                      {milestone.additional_notes && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Notes</Typography>
                          <Typography variant="body2">{milestone.additional_notes}</Typography>
                        </Box>
                      )}

                      {/* Funded stage still with the contractor: say what we are waiting for,
                          and that the report (not an invoice) is what releases the wallet. */}
                      {user?.role === 'client' && hub?.funded && ['pending', 'active', 'in_progress', 'funded'].includes(milestone.status) && (
                        <Alert severity="info" icon={false} sx={{ mb: 2 }} data-testid="stage-waiting-report">
                          <Typography variant="body2">
                            <strong>Waiting for {hub?.contractor?.name || 'the contractor'}'s completion report for this stage.</strong>{' '}
                            Your money stays in the project wallet until then. When the report arrives you'll get a notification:
                            open it, approve it, then tap Release Payment here. No invoice is needed.
                          </Typography>
                        </Alert>
                      )}

                      {/* Review Period Banner for completed milestones with active timer */}
                      {user?.role === 'client' && milestone.status === 'completed' && milestoneTimers[milestone.id] && (
                        <Box sx={{
                          mt: 2, p: 2, borderRadius: 2,
                          background: 'linear-gradient(135deg, #fff3e0 0%, #ffebee 100%)',
                          border: '1px solid #ffab91'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ color: '#bf360c', fontWeight: 700 }}>
                              Review Period Active
                            </Typography>
                            <CountdownTimer autoReleaseAt={milestoneTimers[milestone.id]?.autoReleaseAt} />
                          </Box>
                          <Typography variant="body2" sx={{ color: '#4e342e', mb: 2 }}>
                            Payment will auto-release when the timer expires. Hire a verifier for professional quality assurance before approving.
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFindVerifierDialogOpen(true);
                              }}
                              startIcon={<Person />}
                              sx={{
                                fontSize: '0.75rem',
                                background: brand.gradient,
                                '&:hover': { background: brand.gradientHover }
                              }}
                            >
                              Hire a Verifier — Recommended
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveMilestone(milestone);
                              }}
                              startIcon={<AttachMoney />}
                              sx={{ fontSize: '0.75rem', borderColor: '#bf360c', color: '#bf360c' }}
                            >
                              Approve & Release
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {/* Fallback action buttons for completed milestones without timer */}
                      {user?.role === 'client' && milestone.status === 'completed' && !milestoneTimers[milestone.id] && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFindVerifierDialogOpen(true);
                            }}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Hire a Verifier
                          </Button>
                        </Box>
                      )}

                      {/* Release payment for approved/verified milestones */}
                      {user?.role === 'client' && ['approved', 'verified'].includes(milestone.status) && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReleasePayment(milestone);
                            }}
                            startIcon={<AttachMoney />}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Release Payment
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Contractor Discovery Section */}
      {user?.role === 'client' && milestones.length === 0 && (project?.contractor_id || project?.contractor) ? (
        /* Client view - Contractor assigned but no milestones yet */
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <TrendingUp sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Waiting for Milestones
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Your contractor needs to propose milestones, costs, and the payment schedule.
                Once milestones are defined, you can review them, ask questions, and approve before work starts.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Message />}
                onClick={handleMessageOtherParty}
                sx={{ mt: 1 }}
              >
                Message Contractor
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        /* Client view - No contractor: Show milestone creation + contractor discovery */
        <Box ref={contractorDiscoveryRef} sx={{ mb: 3 }}>
          {/* Work stages summary for clients — stages are proposed by the contractor */}
          {user?.role === 'client' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Work Stages
                </Typography>
                {milestones.length === 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                      Your contractor will propose the work stages, costs, and payment schedule in their quotation. You review and approve them before work starts.
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Add />}
                      onClick={() => setBriefNoteDialogOpen(true)}
                      sx={{ color: brand.primary, textTransform: 'none' }}
                    >
                      Add more details to project brief
                    </Button>
                  </>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {milestones.map((milestone, idx) => (
                      <Paper key={milestone.id} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>
                          {idx + 1}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">{milestone.title || milestone.name || milestone.milestoneDetails?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{milestone.description || milestone.milestoneDetails?.description}</Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={milestone.contractorName || 'Not yet assigned'}
                          color={milestone.contractorName ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Paper>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Next Step: Hire a Contractor
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Choose how you'd like to find the right contractor for your project. You have 3 options:
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Card 1: Browse Contractor Marketplace */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: brand.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <Store sx={{ color: 'white', fontSize: 28 }} />
                  </Box>

                  <Chip
                    label="Recommended"
                    size="small"
                    sx={{
                      bgcolor: '#7c3aed',
                      color: 'white',
                      fontWeight: 600,
                      alignSelf: 'flex-start',
                      mb: 1.5
                    }}
                  />

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Browse Contractor Marketplace
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                    Discover verified contractors with proven track records. Filter by specialization, location, and ratings to find the perfect match for your project.
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, px: 1 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7c3aed' }}>500+</Typography>
                      <Typography variant="caption" color="text.secondary">Contractors</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7c3aed' }}>4.8★</Typography>
                      <Typography variant="caption" color="text.secondary">Avg Rating</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7c3aed' }}>10k+</Typography>
                      <Typography variant="caption" color="text.secondary">Projects Done</Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => navigate(`/contractors?projectId=${id}&projectTitle=${encodeURIComponent(project.title)}`)}
                    sx={{
                      background: brand.gradient,
                      '&:hover': {
                        background: brand.gradientHover
                      },
                      fontWeight: 600,
                      py: 1.5
                    }}
                  >
                    Browse Marketplace
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 2: Invite a Contractor */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: '#2196F3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <PersonAdd sx={{ color: 'white', fontSize: 28 }} />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, mt: 0.5 }}>
                    Invite a Contractor
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                    Already know who you want and they are not on Eqaya? Send a direct invitation to a specific contractor via email. They'll receive your project details and can accept the job.
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, px: 1 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2196F3' }}>Direct</Typography>
                      <Typography variant="caption" color="text.secondary">Outreach</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2196F3' }}>Fast</Typography>
                      <Typography variant="caption" color="text.secondary">Setup</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2196F3' }}>Personal</Typography>
                      <Typography variant="caption" color="text.secondary">Touch</Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={() => {
                      setFindContractorsInitialTab(2);
                      setFindContractorsDialogOpen(true);
                    }}
                    sx={{
                      borderColor: '#2196F3',
                      color: '#2196F3',
                      fontWeight: 600,
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#1976D2',
                        bgcolor: 'rgba(33, 150, 243, 0.04)'
                      }
                    }}
                  >
                    Send Invitation
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 3: Rehire a Previous Contractor */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <History sx={{ color: 'white', fontSize: 28 }} />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, mt: 0.5 }}>
                    Rehire a Previous Contractor
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                    Worked with someone great before? Quickly rehire a contractor you've already collaborated with. Save time with someone who knows your standards.
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, px: 1 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#4CAF50' }}>Trusted</Typography>
                      <Typography variant="caption" color="text.secondary">Partners</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#4CAF50' }}>Proven</Typography>
                      <Typography variant="caption" color="text.secondary">Quality</Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#4CAF50' }}>Quick</Typography>
                      <Typography variant="caption" color="text.secondary">Start</Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={() => {
                      setFindContractorsInitialTab(1);
                      setFindContractorsDialogOpen(true);
                    }}
                    sx={{
                      borderColor: '#4CAF50',
                      color: '#4CAF50',
                      fontWeight: 600,
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#388E3C',
                        bgcolor: 'rgba(76, 175, 80, 0.04)'
                      }
                    }}
                  >
                    View Past Contractors
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Daily Diary Section - shown when a contractor or project manager is assigned */}
      {(project.contractor_id || project.contractor || project.project_manager_id) && (
        <DailyDiarySection
          projectId={id}
          project={project}
          isContractor={user?.role === 'contractor' && project?.contractor_id === user?.id}
          isProjectManager={user?.role === 'project_manager' && project?.project_manager_id === user?.id}
          isClient={user?.role === 'client'}
          milestones={milestones}
        />
      )}

      {/* (Materials Schedule moved to top of page) */}

      {/* Progress Photos Section - contractors manage progress uploads; clients see photos in the brief section above */}
      {user?.role !== 'client' && (projectFiles.photos.length > 0 || (user?.role === 'contractor' && project?.contractor_id === user?.id)) && (
      <Card id="project-photos-section" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Photo color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Progress Photos
              </Typography>
              <Chip
                label={filesLoading ? '...' : projectFiles.photos.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            </Box>
            {user?.role === 'contractor' && 
             project?.contractor_id === user?.id && 
             ['in_progress', 'active', 'assigned', 'accepted'].includes(project?.status) && (
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{
                  background: brand.gradient,
                  '&:hover': {
                    background: brand.gradientHover
                  }
                }}
              >
                Upload Photos
              </Button>
            )}
          </Box>
          
          {filesLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : projectFiles.photos.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {(showAllPhotos ? projectFiles.photos : projectFiles.photos.slice(0, 6)).map((photo, index) => (
                  <Grid item xs={6} sm={4} md={2} key={photo.id || index}>
                    <Paper
                      elevation={2}
                      sx={{
                        position: 'relative',
                        paddingTop: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 6
                        }
                      }}
                      onClick={() => {
                        setSelectedMedia(photo);
                        setMediaViewerOpen(true);
                      }}
                    >
                      <Box
                        component="img"
                        src={photo.url}
                        alt={photo.description || 'Progress photo'}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {photo.milestone && (
                        <Chip
                          label={photo.milestone}
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              {projectFiles.photos.length > 6 && !showAllPhotos && (
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => setShowAllPhotos(true)}
                  endIcon={<Visibility />}
                >
                  View All {projectFiles.photos.length} Photos
                </Button>
              )}
            </>
          ) : (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 6, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'divider'
              }}
            >
              <Photo sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No photos uploaded yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.role === 'contractor' && project?.contractor_id === user?.id
                  ? 'Upload photos to show project progress'
                  : 'Photos will appear here as the contractor uploads progress updates'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Documents Section - contractors manage progress documents; clients see files in the brief section above */}
      {user?.role !== 'client' && (projectFiles.documents.length > 0 || (user?.role === 'contractor' && project?.contractor_id === user?.id)) && (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Documents
              </Typography>
              <Chip
                label={filesLoading ? '...' : projectFiles.documents.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            </Box>
            {user?.role === 'contractor' && 
             project?.contractor_id === user?.id && 
             ['in_progress', 'active', 'assigned', 'accepted'].includes(project?.status) && (
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Documents
              </Button>
            )}
          </Box>
          
          {filesLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : projectFiles.documents.length > 0 ? (
            <Box>
              {projectFiles.documents.map((doc, index) => (
                <Paper
                  key={doc.id || index}
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      elevation: 3,
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                    <InsertDriveFile />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {doc.fileName || 'Document'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Uploaded {formatDate(doc.uploadedAt)} by {doc.uploadedBy}
                      {doc.milestone && ` • ${doc.milestone}`}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => window.open(doc.url, '_blank')}
                    color="primary"
                  >
                    <Download />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 6, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'divider'
              }}
            >
              <Description sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No documents uploaded yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.role === 'contractor' && project?.contractor_id === user?.id
                  ? 'Upload permits, certificates, and other documents'
                  : 'Contracts, permits, and other files will appear here'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Reschedule Site Visit — one step, records what was re-agreed in chat */}
      <Dialog
        open={Boolean(rescheduleRequest)}
        onClose={() => !rescheduling && setRescheduleRequest(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reschedule Site Visit</DialogTitle>
        <DialogContent>
          {rescheduleError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRescheduleError('')}>{rescheduleError}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Agreed a new time with the contractor? Set it here — they'll be notified immediately.
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            label="New visit date & time"
            value={rescheduleTime}
            onChange={(e) => setRescheduleTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Zimbabwe (Harare) time"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleRequest(null)} disabled={rescheduling}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitReschedule}
            disabled={rescheduling || !rescheduleTime}
          >
            {rescheduling ? 'Saving…' : 'Reschedule Visit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assessment Response Dialog */}
      <Dialog
        open={assessmentResponseDialogOpen}
        onClose={() => setAssessmentResponseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Respond to Assessment Request
          <Typography variant="body2" color="text.secondary">
            Provide the details requested, accept a site visit time, or propose new options.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAssessmentRequest && (
            <Stack spacing={3}>
              <Alert severity="info">
                Upload plans, photos, or documents to this project first, then mark which requested items you have provided. The contractor will be notified.
              </Alert>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Requested by contractor
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {safeArray(selectedAssessmentRequest.assessment_items).map((item) => (
                    <Chip key={item} label={ASSESSMENT_LABELS[item] || item} variant="outlined" />
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Mark details you have provided
                </Typography>
                <Grid container spacing={1}>
                  {safeArray(selectedAssessmentRequest.assessment_items)
                    .filter((item) => item !== 'site_visit' && item !== 'client_call')
                    .map((item) => (
                      <Grid item xs={12} sm={6} key={item}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={assessmentResponse.providedItems.includes(item)}
                              onChange={() => toggleProvidedItem(item)}
                            />
                          }
                          label={ASSESSMENT_LABELS[item] || item}
                        />
                      </Grid>
                    ))}
                </Grid>
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={() => setUploadDialogOpen(true)}
                  sx={{ mt: 1 }}
                >
                  Upload Project Files
                </Button>
              </Box>

              {safeArray(selectedAssessmentRequest.availability_windows).length > 0 && (
                <FormControl fullWidth>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Accept a suggested site visit window
                  </Typography>
                  <Select
                    displayEmpty
                    value={assessmentResponse.acceptedWindow}
                    onChange={(e) => setAssessmentResponse({ ...assessmentResponse, acceptedWindow: e.target.value })}
                  >
                    <MenuItem value="">Do not accept a suggested window yet</MenuItem>
                    {safeArray(selectedAssessmentRequest.availability_windows).map((windowValue) => (
                      <MenuItem key={windowValue} value={windowValue}>
                        {formatDateTime(windowValue)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Or propose different visit windows (Zimbabwe / Harare time)
                </Typography>
                <Grid container spacing={1.5}>
                  {assessmentResponse.proposedWindows.map((windowValue, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        label={`Option ${index + 1}`}
                        value={windowValue}
                        onChange={(e) => {
                          const nextWindows = [...assessmentResponse.proposedWindows];
                          nextWindows[index] = e.target.value;
                          setAssessmentResponse({ ...assessmentResponse, proposedWindows: nextWindows, acceptedWindow: '' });
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message to Contractor"
                placeholder="Explain what you uploaded, which visit option works, or what alternative you are proposing."
                value={assessmentResponse.responseMessage}
                onChange={(e) => setAssessmentResponse({ ...assessmentResponse, responseMessage: e.target.value })}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={assessmentResponse.decline}
                    onChange={(e) => setAssessmentResponse({ ...assessmentResponse, decline: e.target.checked })}
                  />
                }
                label="Decline this request"
              />
              {assessmentResponse.decline && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>Why not? One tap helps the contractor know what to do next.</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      ['timing', 'Not the right time yet'],
                      ['other_contractor', 'Going with someone else'],
                      ['not_going_ahead', 'Project on hold / not going ahead'],
                      ['quote_from_brief', 'Just quote from the brief, no visit needed'],
                    ].map(([v, label]) => (
                      <Chip key={v} label={label} clickable
                        color={assessmentResponse.declineReason === v ? 'primary' : 'default'}
                        variant={assessmentResponse.declineReason === v ? 'filled' : 'outlined'}
                        onClick={() => setAssessmentResponse({ ...assessmentResponse, declineReason: assessmentResponse.declineReason === v ? '' : v })} />
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssessmentResponseDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<FactCheck />}
            onClick={handleRespondToAssessmentRequest}
          >
            Send Response
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Upload Dialog */}
      <FileUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        projectId={id}
        milestones={milestones}
        title={user?.role === 'client' ? 'Upload Project Reference Files' : 'Upload Progress Files'}
        subtitle={user?.role === 'client'
          ? 'Add site photos, inspiration images, plans, or documents so contractors understand your brief.'
          : 'Add photos, videos, or documents to track project progress'}
        onUploadComplete={() => {
          loadProjectFiles();
          setUploadDialogOpen(false);
          setSuccess('Files uploaded successfully!');
        }}
      />

      {/* Owner Brief Note Dialog */}
      <Dialog
        open={briefNoteDialogOpen}
        onClose={() => !savingBriefNote && setBriefNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setBriefNoteDialogOpen(false)} />
        <DialogTitle>
          Add to Project Brief
          <Typography variant="body2" color="text.secondary">
            Add plain-language details for contractors. This will update the project overview, not create a technical milestone.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Note or clarification"
              placeholder="Example: I want cabinets like the second kitchen photo, but with darker countertops."
              value={briefNote}
              onChange={(e) => setBriefNote(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => {
                setBriefNoteDialogOpen(false);
                setUploadDialogOpen(true);
              }}
              sx={{ alignSelf: 'flex-start', borderColor: brand.primary, color: brand.primary }}
            >
              Upload reference files instead
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBriefNoteDialogOpen(false)} disabled={savingBriefNote}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBriefNote}
            disabled={savingBriefNote || !briefNote.trim()}
            sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
          >
            {savingBriefNote ? 'Saving...' : 'Save to Brief'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Viewer */}
      <MediaViewer
        open={mediaViewerOpen}
        onClose={() => {
          setMediaViewerOpen(false);
          setSelectedMedia(null);
        }}
        media={selectedMedia}
        allMedia={[...projectFiles.photos, ...projectFiles.videos]}
      />

      {/* Payment-stage creation for the assigned contractor */}
      {user?.role === 'contractor' && (
        <MilestoneCreationDialog
          open={createMilestoneDialogOpen}
          onClose={() => setCreateMilestoneDialogOpen(false)}
          projectId={id}
          onMilestoneCreated={async () => {
            await loadProjectData();
            setCreateMilestoneDialogOpen(false);
            setSuccess('Stage added. The client funds it into escrow before you start it.');
          }}
        />
      )}

      {/* Proposal Review Dialog */}
      {showProposalReview && selectedProposal && (
        <ProposalReviewDialog
          open={showProposalReview}
          onClose={() => {
            setShowProposalReview(false);
            setSelectedProposal(null);
          }}
          proposal={selectedProposal}
          onProposalAction={async (action) => {
            await loadProjectData();
            setShowProposalReview(false);
            setSelectedProposal(null);
            if (action === 'accepted') {
              setSuccess('Proposal accepted! The contractor has been hired. Fund your project to get started.');
              // Prompt escrow setup after a short delay so user reads the success message
              setTimeout(() => setEscrowSetupDialogOpen(true), 2000);
            }
            else if (action === 'rejected') setSuccess('Proposal declined.');
            else if (action === 'changes_requested') setSuccess('Change request sent to contractor.');
          }}
        />
      )}

      {/* Find Contractors Dialog */}
      <FindContractorsDialog
        open={findContractorsDialogOpen}
        onClose={() => setFindContractorsDialogOpen(false)}
        project={project}
        initialTab={findContractorsInitialTab}
        onContractorSelected={(contractor) => {
          setSelectedContractor(contractor);
          setFindContractorsDialogOpen(false);
          // Open escrow setup dialog after contractor selection
          setEscrowSetupDialogOpen(true);
        }}
      />

      {/* Find Verifier Dialog */}
      <FindVerifierDialog
        open={findVerifierDialogOpen}
        onClose={() => setFindVerifierDialogOpen(false)}
        project={project}
        onVerifierHired={() => {
          setFindVerifierDialogOpen(false);
          setSuccess('Verifier hire request sent successfully!');
          loadProjectData();
        }}
      />

      {/* Post-visit check-in: two answers, one tap */}
      <Dialog open={Boolean(visitFeedbackRequestId)} onClose={() => setVisitFeedbackRequestId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>How did the visit go?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The contractor marked their visit as done. Tell them where they stand — one tap, nothing to type.
          </Typography>
          <Stack spacing={1}>
            {[
              ['Yes, send me the quotation', { wantsQuote: true }, 'contained'],
              ['No thanks, not going ahead with them', { wantsQuote: false }, 'outlined'],
              ["The visit didn't happen as planned", { wantsQuote: false, didHappen: false }, 'outlined'],
            ].map(([label, payload, variant]) => (
              <Button key={label} variant={variant} disabled={visitFeedbackSaving} sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                onClick={async () => {
                  setVisitFeedbackSaving(true);
                  try {
                    await api.post(`/projects/${id}/assessment-requests/${visitFeedbackRequestId}/visit-feedback`, payload);
                    setSuccess(payload.wantsQuote ? 'Thanks — the contractor has been told you are waiting for their quotation.' : 'Thanks — the contractor has been told.');
                    setVisitFeedbackRequestId(null);
                    loadProjectData();
                  } catch (err) {
                    setError(err.response?.data?.message || 'Could not save your answer');
                  } finally { setVisitFeedbackSaving(false); }
                }}>
                {label}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitFeedbackRequestId(null)}>Later</Button>
        </DialogActions>
      </Dialog>

      {/* Escrow Setup Dialog */}
      <ListingOutcomeDialog
        open={outcomeDialogOpen}
        onClose={() => setOutcomeDialogOpen(false)}
        projectId={id}
        projectTitle={project?.title}
        currentStatus={project?.status}
        onDone={async (outcome) => {
          await loadProjectData();
          setSuccess(outcome === 'still_deciding'
            ? (project?.status === 'cancelled' ? 'Listing reopened — contractors can quote again.' : 'Thanks — we\'ll keep it open and check in again in a month.')
            : 'Thanks for telling us. The listing is closed; reopen it any time.');
        }}
      />

      <EscrowSetupDialog
        open={escrowSetupDialogOpen}
        onClose={() => setEscrowSetupDialogOpen(false)}
        project={project}
        // The assigned contractor comes back on the project as user_profiles
        // (user_id), which is what the wallet must be linked to.
        contractor={selectedContractor || (project?.contractor?.user_id ? { id: project.contractor.user_id } : (project?.contractor_id ? { id: project.contractor_id } : null))}
        skipTerms={escrowSkipTerms}
        // Stage-by-stage ask from the hub: next stage's shortfall + total.
        suggestion={hub?.deposit || null}
        onSuccess={async (escrow) => {
          await loadProjectData();
          setEscrowSetupDialogOpen(false);
          setSuccess(escrowSkipTerms ? 'Wallet funded successfully!' : 'Wallet created and funded successfully!');
        }}
      />
      
      {/* Simple Verification Request Dialog */}
      {/* Generic confirmation dialog (replaces window.confirm) */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{confirmDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {confirmDialog?.body}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmDialog(null)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmDialog?.confirmColor || 'primary'}
            onClick={() => {
              const fn = confirmDialog?.onConfirm;
              setConfirmDialog(null);
              fn?.();
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {confirmDialog?.confirmLabel || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {verificationDialog && selectedMilestoneForVerification && (
        <AISimpleVerificationRequestDialog
          open={verificationDialog}
          onClose={() => {
            setVerificationDialog(false);
            setSelectedMilestoneForVerification(null);
          }}
          projectId={id}
          projectName={project?.title}
          projectLocation={project?.location}
          milestoneId={selectedMilestoneForVerification.id}
          milestoneTitle={selectedMilestoneForVerification.title}
          onComplete={(verificationData) => {
            setVerificationDialog(false);
            setSelectedMilestoneForVerification(null);
            setSuccess('Verification request created! A verifier will be assigned shortly.');
          }}
        />
      )}
    </Container>
  );
};

export default ProjectDetailPage;
